import AntIcon from "@/components/icons/AntIcon";
import ContentWrapper from "@/components/wrappers/ContentWrapper";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { cssVariables } from "@/utils/utils";
import { Tabs, TabsProps } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { Permission } from "../../../common/security/enums";
import { useHasPermissions, useIsCurrentAgentTypePlus } from "../../auth/hooks";
import { FinancialMediationDocumentPrefill } from "../../financialmediation/components/FinancialMediationDocumentPrefill";
import { FinancialMediationFilterView } from "../../financialmediation/components/views/FinancialMediationFilterView";
import { FinancialMediationTableView } from "../../financialmediation/components/views/FinancialMediationTableView";
import { FinancialMediationFilterPageRequest } from "../../financialmediation/types";
import { ContractTerminationForm } from "../components/forms/ContractTerminationForm";
import { RegisterVehicleDataForm } from "../components/forms/RegisterVehicleDataForm";
import { CONTRACT_FORMS_TABS } from "../utils";

export const ContractFormsContainer = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [urlParams] = useSearchParams();
  const tabKey = urlParams.get("tab") ?? CONTRACT_FORMS_TABS.MEDIATION;

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const searchParams = useFilterSearchParams<FinancialMediationFilterPageRequest>({
    params: ["includeAssigned", "sectors"],
  });

  const hasInsuranceReadPermission = useHasPermissions(Permission.INSURANCE_READ);

  const moveViewToTab = (tabKey: string) => {
    const tab = tabKey === CONTRACT_FORMS_TABS.MEDIATION ? undefined : tabKey;
    navigate({ search: URLQuery.searchParamsToString({ searchParams: { tab } }) }, { replace: true });
  };

  useEffect(() => {
    if (
      (tabKey === CONTRACT_FORMS_TABS.TERMINATION || tabKey === CONTRACT_FORMS_TABS.REGISTER_VEHICLE_DATA) &&
      !hasInsuranceReadPermission
    ) {
      moveViewToTab(CONTRACT_FORMS_TABS.MEDIATION);
    }
  }, [tabKey, hasInsuranceReadPermission]);

  const getItems = (): TabsProps["items"] => {
    const items: TabsProps["items"] = [];

    items.push({
      key: CONTRACT_FORMS_TABS.MEDIATION,
      icon: <AntIcon type="profile" />,
      label: t("financialMediation.titles.listTab"),
      children: isTopAgentPlus ? (
        <FinancialMediationDocumentPrefill />
      ) : (
        <>
          <FinancialMediationFilterView searchParams={searchParams} />

          <FinancialMediationTableView searchParams={searchParams} />
        </>
      ),
    });

    if (hasInsuranceReadPermission) {
      items.push({
        key: CONTRACT_FORMS_TABS.TERMINATION,
        icon: <AntIcon type="stop" />,
        label: t("contractTermination.titles.page"),
        children: <ContractTerminationForm />,
      });

      items.push({
        key: CONTRACT_FORMS_TABS.REGISTER_VEHICLE_DATA,
        icon: <AntIcon type="car" />,
        label: t("registerVehicleData.titles.page"),
        children: <RegisterVehicleDataForm />,
      });
    }

    return items;
  };

  return (
    <ContentWrapper>
      <Tabs
        tabBarStyle={{
          backgroundColor: "white",
          padding: "0 24px",
          marginBottom: 24,
          borderRadius: 8,
          boxShadow: `0 1px 3px 0 ${cssVariables.colorShadow}, 0 1px 2px 0 ${cssVariables.colorShadow}`,
          border: `1px solid ${cssVariables.colorHover}`,
          overflow: "hidden",
        }}
        activeKey={tabKey}
        onChange={moveViewToTab}
        items={getItems()}
      />
    </ContentWrapper>
  );
};
